import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/hizmetler/kampanya-yonetimi';
const pageUrl = `${siteUrl}${pagePath}`;

const campaignFAQItems = [
  {
    question: 'Kampanya yönetimi nedir?',
    answer:
      'Kampanya yönetimi, bir markanın belirli bir hedefe yönelik tüm iletişim faaliyetlerinin stratejik planlama, yaratıcı uygulama ve performans takibi sürecidir. Bir reklam serisinden çok daha fazlasıdır; tutarlı bir anlatının tüm kanallarda yönetilmesidir.',
  },
  {
    question: 'İtibar yönetimi neden önemlidir?',
    answer:
      'Dijital çağda bir markanın itibarı, kontrol edemediğiniz platformlarda şekillenir. İtibar yönetimi, markanız hakkındaki algıyı proaktif olarak şekillendirmek ve kriz anlarında doğru iletişimi yönetmek için kritiktir.',
  },
  {
    question: 'Kriz iletişimi nasıl yönetilir?',
    answer:
      'Kriz iletişimi hız, şeffaflık ve tutarlılık gerektirir. Önceden hazırlanmış senaryolar, net bir onay mekanizması ve tüm kanallarda eş zamanlı iletişim yönetimiyle krizi kontrollü şekilde yönetiriz.',
  },
  {
    question: 'Siyasal iletişim danışmanlığı veriyor musunuz?',
    answer:
      'Evet. Siyasal kampanyalarda stratejik iletişim danışmanlığı, seçmen davranışı analizi, mesaj stratejisi ve dijital kampanya yönetimi hizmetleri sunuyoruz.',
  },
  {
    question: 'Bir kampanya ne kadar sürede hazırlanır?',
    answer:
      'Kampanyanın ölçeğine bağlı olarak strateji ve kreatif hazırlık 2-6 hafta sürer. Lansman kampanyaları daha uzun hazırlık gerektirirken, reaktif kampanyalar 1-2 haftada hayata geçirilebilir.',
  },
  {
    question: 'Sadece kampanya yönetimi için çalışabilir miyiz?',
    answer:
      'Evet. Mevcut marka stratejiniz ve ekibiniz varsa, sadece kampanya stratejisi ve yönetimi için proje bazlı çalışabiliriz. Ancak kampanyanın marka stratejisiyle tutarlı olması için mevcut stratejinizi de inceleriz.',
  },
];

const faqSchema = getFAQSchema(campaignFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'Kampanya & İtibar Yönetimi Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'Kampanya stratejisi, lansman yönetimi, itibar yönetimi ve kriz iletişimiyle markanızın anlatısını yönetiyoruz. BIFF Digital kampanyaları strateji ve yaratıcılıkla birlikte yönetir.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'Kampanya & İtibar Yönetimi',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Kampanya Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kampanya Stratejisi', description: 'Hedef odaklı, bütüncül kampanya stratejisi geliştirme' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lansman Kampanyaları', description: 'Marka ve ürün lansmanları için 360 derece kampanya yönetimi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Anlatı Yönetimi', description: 'Marka anlatısının tutarlı şekilde tüm kanallarda yönetilmesi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'İtibar & Kriz İletişimi', description: 'Proaktif itibar yönetimi ve kriz anında stratejik iletişim' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${siteUrl}/hizmetler` },
        { '@type': 'ListItem', position: 3, name: 'Kampanya & İtibar Yönetimi', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function KampanyaYonetimi() {
  useEffect(() => {
    document.title = 'Kampanya & İtibar Yönetimi | Lansman & Kriz İletişimi | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="section-padding pb-0 md:pb-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">HİZMETLER</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-8">
              Kampanya &amp;<br />İtibar Yönetimi
            </h1>
            <div className="h-px w-full bg-gradient-to-r from-white/15 via-white/5 to-transparent mb-10" />
            <p className="text-xl md:text-2xl text-foreground-200 font-medium leading-relaxed mb-6">
              Bir Kampanya Bir Reklam Değil.<br />
              <span className="text-primary-500">Bir Anlatıdır.</span>
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-2xl speakable-summary">
              Kampanya önce medyada değil, masada kazanılır. Stratejisi olmayan kampanya, bütçesi olan gürültüdür. Biz markanızın anlatısını stratejiyle inşa ediyor, yaratıcılıkla hayata geçiriyor ve tüm kanallarda tutarlı şekilde yönetiyoruz.
            </p>
          </div>
        </section>

        {/* Kampanya Önce Masada Kazanılır */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Kampanya Önce Medyada Değil,<br />Masada Kazanılır.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Büyük bütçeler, büyük fikirlerin yerini tutmaz. Bir kampanyanın başarısı, yayına çıkmadan çok önce, strateji masasında belirlenir. Kime söyleyeceksiniz, ne söyleyeceksiniz, nerede söyleyeceksiniz ve en önemlisi — neden söyleyeceksiniz?
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed">
                Büyük bir fikir varsa,<br />onu büyük bir kampanyaya dönüştürelim.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding bg-background-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">NE YAPIYORUZ?</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Anlatıyı İnşa Etmekten<br />Krizi Yönetmeye
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-flashlight-line', title: 'Kampanya Stratejisi', desc: 'Hedef kitle, mesaj, kanal seçimi, zamanlama ve KPI çerçevesinde bütüncül kampanya stratejisi geliştiriyoruz. Her kampanya bir öncekinden ders alır.' },
                { icon: 'ri-rocket-2-line', title: 'Lansman Kampanyaları', desc: 'Marka lansmanı, ürün lansmanı veya yeniden konumlandırma. Lansman, markanın ilk izlenimidir — ve ilk izlenimi ikinci kez yaratamazsınız.' },
                { icon: 'ri-chat-quote-line', title: 'Anlatı Yönetimi', desc: 'Markanızın anlatısını tüm kanallarda tutarlı şekilde yönetiyoruz. Sosyal medyadan basın bültenine, reklamdan web sitesine kadar her temas noktası aynı hikayeyi anlatmalı.' },
                { icon: 'ri-shield-check-line', title: 'İtibar Yönetimi', desc: 'Dijitalde markanız hakkında ne konuşulduğunu izliyor, algıyı proaktif olarak şekillendiriyor ve gerektiğinde müdahale ediyoruz. İtibar inşa edilmesi yıllar, kaybedilmesi dakikalar süren bir varlıktır.' },
                { icon: 'ri-alert-line', title: 'Kriz İletişimi', desc: 'Kriz anında hız, şeffaflık ve tutarlılıkla hareket ediyoruz. Önceden hazırlanmış kriz senaryoları ve hızlı onay mekanizmalarıyla krizi yönetilebilir hale getiriyoruz.' },
                { icon: 'ri-government-line', title: 'Siyasal İletişim', desc: 'Seçmen davranışı analizi, mesaj stratejisi, dijital kampanya yönetimi ve saha iletişimiyle siyasal kampanyaları veri odaklı yönetiyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Süreç */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              Kampanya Sürecimiz
            </h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Brief & Araştırma', desc: 'Hedef, hedef kitle, bütçe, zamanlama ve başarı kriterlerini netleştiriyoruz. Pazar ve rakip analiziyle stratejik zemini hazırlıyoruz.' },
                { step: '02', title: 'Strateji ve Kreatif Konsept', desc: 'Büyük fikri buluyor, mesaj mimarisini kuruyor ve yaratıcı konsepti geliştiriyoruz. Strateji ve kreatif aynı masada şekilleniyor.' },
                { step: '03', title: 'Prodüksiyon ve Uygulama', desc: 'İçerik üretimi, medya planlaması ve tüm kanallarda eş zamanlı uygulama. Her kanal için optimize edilmiş, ancak tutarlı bir anlatı.' },
                { step: '04', title: 'Ölçüm ve Optimizasyon', desc: 'Kampanya canlıyken performansı izliyor, gerektiğinde anlık optimizasyon yapıyoruz. Kampanya bitiminde detaylı rapor ve öğrenimleri belgeliyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={campaignFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="KAMPANYA & İTİBAR YÖNETİMİ"
          sectionSummary="Kampanya ve itibar yönetimi hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Büyük bir fikir varsa,<br />
                  <span className="text-primary-500">onu büyük bir kampanyaya</span> dönüştürelim.
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  İlk strateji görüşmesinde fikrinizi anlatın. Nasıl bir kampanyaya dönüşebileceğini birlikte görelim.
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Strateji Görüşmesi Planla
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}